import { Fragment, useMemo, type CSSProperties, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { Check, FileText } from 'lucide-react';
import { WandSparkles } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import {
  isPart,
  type BuildView,
  type MessageView,
  type OutlineSnapshotPart,
  type PartOf,
  type ResultView,
} from '../../api/authorWithAiApi';
import { OutlineBox } from './OutlineBox';
import { QuestionOptionsList, type QuestionOptionsListProps } from './QuestionOptionsList';
import { ScenarioOptionsList, type ScenarioOptionsListProps } from './ScenarioOptionsList';
import { attachmentMeta, lessonPosition } from './threadModel';
import { CONVERSATION_COPY, RESULT_COPY, SELECTION_COPY } from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';
import type { LibraryLabel, LibraryRef } from './useLibraryLabels';

/** Paragraphs split on blank lines; a run of "- " lines becomes a bulleted list. No markdown. */
export function TextPart({ text, styles }: { text: string; styles: Record<string, CSSProperties> }) {
  const blocks = text.split(/\n\s*\n/).map((block) => block.replace(/^\n+|\n+$/g, '')).filter(Boolean);
  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n');
        const groups: Array<{ list: boolean; lines: string[] }> = [];
        for (const line of lines) {
          const isItem = line.startsWith('- ');
          const last = groups[groups.length - 1];
          if (last && last.list === isItem) last.lines.push(isItem ? line.slice(2) : line);
          else groups.push({ list: isItem, lines: [isItem ? line.slice(2) : line] });
        }
        return (
          <Fragment key={blockIndex}>
            {groups.map((group, groupIndex) => (group.list
              ? <ul key={groupIndex} style={styles.bulletList}>{group.lines.map((line, index) => <li key={index}>{line}</li>)}</ul>
              : <p key={groupIndex} style={styles.paragraph}>{group.lines.join('\n')}</p>))}
          </Fragment>
        );
      })}
    </>
  );
}

function SelectionTile({ part, labelFor, styles }: { part: PartOf<'selection_change'>; labelFor: (ref: LibraryRef) => LibraryLabel; styles: Record<string, CSSProperties> }) {
  const kind = part.decision === 'video' ? 'video' as const : 'question' as const;
  return (
    <div style={styles.tile} data-testid="selection-tile">
      {part.status === 'skipped' || part.libraryIds.length === 0
        ? <span>{SELECTION_COPY.skipped(part.decision)}</span>
        : part.libraryIds.map((libraryId) => <span key={libraryId} style={styles.tileItem}>{labelFor({ kind, libraryId }).label}</span>)}
    </div>
  );
}

/** The author's uploaded file: name, then size and unit count ("4.2 MB · 38 slides"). */
function AttachmentTile({ part, styles }: { part: PartOf<'attachment'>; styles: Record<string, CSSProperties> }) {
  return (
    <div style={{ ...styles.authorBubble, ...styles.fileTile }} data-testid="attachment-tile">
      <FileText size="1em" strokeWidth={1.75} aria-hidden="true" />
      <span style={styles.fileTileText}>
        <span>{part.fileName}</span>
        <span style={styles.fileMeta}>{attachmentMeta(part)}</span>
      </span>
    </div>
  );
}

function ApprovalLine({ part, view, styles }: { part: PartOf<'approval'>; view: BuildView; styles: Record<string, CSSProperties> }) {
  const text = part.scope === 'course'
    ? SELECTION_COPY.approvedCourse(part.mode)
    : SELECTION_COPY.approvedLesson(view.workingState.outline && view.workingState.outline.lessons.length > 1 ? lessonPosition(view, part.targetId) : null);
  return (
    // Unbubbled, so it centers on the 36px avatar itself (the avatar carries a 3px nudge for bubble padding).
    <span style={{ ...styles.approvalLine, marginTop: '9px' }}>
      <Check size="1em" strokeWidth={1.75} aria-hidden="true" />
      {text}
    </span>
  );
}

const outlineLessonTitle = (view: BuildView, outlineLessonId: string): string =>
  view.workingState.outline?.lessons.find((lesson) => lesson.id === outlineLessonId)?.title
  ?? view.progress?.lessons.find((lesson) => lesson.outlineLessonId === outlineLessonId)?.title
  ?? RESULT_COPY.lesson;

/** Links to every lesson and the course of one run's output, course first. */
export function ResultLinks({ view, result, styles }: { view: BuildView; result: ResultView; styles: Record<string, CSSProperties> }) {
  return (
    <ul style={styles.progressList}>
      {result.course && (
        <li style={styles.progressRow}>
          <span><strong>{result.course.displayId}</strong> {view.workingState.outline?.title ?? RESULT_COPY.course}{result.course.ready ? '' : ` ${RESULT_COPY.partial}`}</span>
          <Link to={result.course.editorPath} style={styles.resultLink}>{RESULT_COPY.openInEditor}</Link>
        </li>
      )}
      {result.lessons.map((lesson) => (
        <li key={lesson.lessonId} style={styles.progressRow}>
          <span><strong>{lesson.displayId}</strong> {outlineLessonTitle(view, lesson.outlineLessonId)}{lesson.ready ? '' : ` ${RESULT_COPY.partial}`}</span>
          <Link to={lesson.editorPath} style={styles.resultLink}>{RESULT_COPY.openInEditor}</Link>
        </li>
      ))}
    </ul>
  );
}

function ResultCard({ part, view, accountName, styles }: { part: PartOf<'result_reference'>; view: BuildView; accountName: string | null; styles: Record<string, CSSProperties> }) {
  const result = view.results.find((entry) => entry.runId === part.runId);
  return (
    <div style={styles.card2} data-testid="result-card">
      <strong>{result && (result.course ? 1 : 0) + result.lessons.length === 1 ? RESULT_COPY.headingOne : RESULT_COPY.heading}</strong>
      {result && <ResultLinks view={view} result={result} styles={styles} />}
      {view.accountId !== null && <p style={styles.muted}>{RESULT_COPY.accountNote(accountName ?? `account ${view.accountId}`)}</p>}
    </div>
  );
}

export function AssistantAvatar({ styles }: { styles: Record<string, CSSProperties> }) {
  return (
    <span style={{ ...styles.avatar, ...styles.avatarAssistant }} aria-label={CONVERSATION_COPY.assistant} role="img">
      <WandSparkles size="1em" strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
}

export function MessageRow({ role, initials, children }: { role: 'author' | 'assistant'; initials: string; children: ReactNode }) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  return (
    <div style={{ ...styles.messageRow, ...(role === 'author' ? styles.messageRowAuthor : {}) }}>
      {role === 'assistant'
        ? <AssistantAvatar styles={styles} />
        : <span style={{ ...styles.avatar, ...styles.avatarAuthor, marginTop: '3px' }} aria-label={CONVERSATION_COPY.you} role="img">{initials}</span>}
      <div style={{ ...styles.messageBody, ...(role === 'author' ? styles.messageBodyAuthor : { paddingTop: '6px' }) }}>{children}</div>
    </div>
  );
}

export interface MessageItemProps {
  message: MessageView;
  view: BuildView;
  initials: string;
  labelFor: (ref: LibraryRef) => LibraryLabel;
  accountName: string | null;
  previousSnapshot: OutlineSnapshotPart | null;
  /** Set on the message holding the most recent outline box. */
  latestBox?: { staleNotice: boolean };
  boxRef?: Ref<HTMLDivElement>;
  /** Set on the latest assistant message: its question list rows submit picks. */
  questionList?: Pick<QuestionOptionsListProps, 'onPick' | 'disabled' | 'rowErrors'>;
  /** Set on the latest assistant message: its scenario list rows open the preview and submit picks. */
  scenarioList?: Pick<ScenarioOptionsListProps, 'onPick' | 'disabled' | 'rowErrors'>;
}

/**
 * Renders a message from its parts only; unknown parts are skipped,
 * suggestions, taxonomy options and concept options render as chips
 * elsewhere, an attachment renders as the author's file tile, and a message
 * made only of hidden parts (the entry of an "Ask a question" or "Lead with
 * a video scenario" build) renders nothing.
 */
export function MessageItem({ message, view, initials, labelFor, accountName, previousSnapshot, latestBox, boxRef, questionList, scenarioList }: MessageItemProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const rendered = message.parts.map((part, index) => {
    if (isPart(part, 'text')) {
      return message.role === 'author'
        ? <div key={index} style={styles.authorBubble}>{part.text}</div>
        : <TextPart key={index} text={part.text} styles={styles} />;
    }
    if (isPart(part, 'selection_change')) return <SelectionTile key={index} part={part} labelFor={labelFor} styles={styles} />;
    if (isPart(part, 'browse_choice')) return <div key={index} style={styles.authorBubble}>{part.label}</div>;
    if (isPart(part, 'attachment')) return <AttachmentTile key={index} part={part} styles={styles} />;
    if (isPart(part, 'question_suggestions')) return <QuestionOptionsList key={index} buildId={view.id} part={part} reasons />;
    if (isPart(part, 'video_suggestion')) return <ScenarioOptionsList key={index} part={{ type: 'scenario_options', conceptId: part.conceptId, label: part.label, total: 1, items: [part.item] }} suggestion={{ category: part.category, reason: part.reason }} />;
    if (isPart(part, 'question_options')) return <QuestionOptionsList key={index} buildId={view.id} part={part} {...(questionList ?? {})} />;
    if (isPart(part, 'scenario_options')) return <ScenarioOptionsList key={index} part={part} {...(scenarioList ?? {})} />;
    if (isPart(part, 'approval')) return <ApprovalLine key={index} part={part} view={view} styles={styles} />;
    if (isPart(part, 'outline_snapshot')) {
      return (
        <OutlineBox
          key={index}
          ref={latestBox ? boxRef : undefined}
          view={view}
          part={part}
          previous={previousSnapshot}
          labelFor={labelFor}
          {...(latestBox ? { latest: latestBox } : {})}
        />
      );
    }
    if (isPart(part, 'result_reference')) return <ResultCard key={index} part={part} view={view} accountName={accountName} styles={styles} />;
    return null;
  }).filter((node) => node !== null);
  if (rendered.length === 0) return null;
  return (
    <div data-testid={`message-${message.sequence}`} data-role={message.role}>
      <MessageRow role={message.role} initials={initials}>{rendered}</MessageRow>
    </div>
  );
}
